import { createBrowserRouter, redirect, type RouteObject } from 'react-router';
import { LEGACY_ROUTES, LOCALES, type Locale } from '@ceo/shared';
import { SiteLayout } from '@/components/layout/SiteLayout';
import { NotFound, PageLoader, RouteError } from '@/components/ui/Feedback';
import { localePath } from '@/lib/locale';
import { requireAdmin, requireRole } from '@/admin/auth';
import { q, queryClient } from '@/lib/queries';

const siteRoutes = (locale: Locale): RouteObject => ({
  path: localePath(locale, '/'),
  element: <SiteLayout locale={locale} />,
  handle: { locale },
  errorElement: <SiteLayout locale={locale} />,
  hydrateFallbackElement: <PageLoader />,
  // `?lang=sq` links from the old site land on the Albanian version.
  loader: async ({ request }) => {
    const url = new URL(request.url);
    if (locale === 'en' && url.searchParams.get('lang') === 'sq') {
      url.searchParams.delete('lang');
      throw redirect(`/sq${url.pathname === '/' ? '' : url.pathname}${url.search}`);
    }
    await queryClient.ensureQueryData(q.settings(locale));
    return null;
  },
  children: [
    {
      errorElement: <RouteError />,
      children: [
        { index: true, lazy: () => import('@/pages/home/HomePage') },
        { path: 'company-background', lazy: () => import('@/pages/about/CompanyBackgroundPage') },
        { path: 'our-clients', lazy: () => import('@/pages/about/ClientsPage') },
        { path: 'our-staff', lazy: () => import('@/pages/about/StaffPage') },
        { path: 'services', lazy: () => import('@/pages/services/ServicesPage') },
        { path: 'services/:slug', lazy: () => import('@/pages/services/ServiceDetailPage') },
        { path: 'events/:year?', lazy: () => import('@/pages/events/OurEventsPage') },
        { path: 'other-events/:year?', lazy: () => import('@/pages/events/OtherEventsPage') },
        { path: 'contact-us', lazy: () => import('@/pages/contact/ContactPage') },
        { path: 'data-protection', lazy: () => import('@/pages/legal/DataProtectionPage') },
        {
          path: ':legacy',
          loader: ({ params }) => {
            const key = params.legacy!.replace(/\/$/, '');
            const target = Object.hasOwn(LEGACY_ROUTES, key) ? LEGACY_ROUTES[key] : undefined;
            if (target) throw redirect(localePath(locale, target));
            throw new Response('Not found', { status: 404 });
          },
          element: <NotFound />,
        },
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
});

const adminRoutes: RouteObject = {
  path: '/admin',
  errorElement: <RouteError />,
  hydrateFallbackElement: <PageLoader />,
  children: [
    { path: 'login', lazy: () => import('@/admin/LoginPage') },
    {
      middleware: [requireAdmin],
      lazy: () => import('@/admin/AdminLayout'),
      children: [
        { index: true, lazy: () => import('@/admin/DashboardPage') },
        { path: 'events', lazy: () => import('@/admin/EventsAdmin') },
        { path: 'events/new', lazy: () => import('@/admin/EventForm') },
        { path: 'events/:id', lazy: () => import('@/admin/EventForm') },
        { path: 'services', lazy: () => import('@/admin/ServicesAdmin') },
        { path: 'services/new', lazy: () => import('@/admin/ServiceForm') },
        { path: 'services/:id', lazy: () => import('@/admin/ServiceForm') },
        { path: 'pages', lazy: () => import('@/admin/PagesAdmin') },
        { path: 'pages/:slug/:locale', lazy: () => import('@/admin/PageEditor') },
        { path: 'stats', lazy: () => import('@/admin/StatsAdmin') },
        { path: 'media', lazy: () => import('@/admin/MediaAdmin') },
        { path: 'gallery', lazy: () => import('@/admin/GalleryAdmin') },
        { path: 'videos', lazy: () => import('@/admin/VideosAdmin') },
        { path: 'settings', lazy: () => import('@/admin/SettingsPage') },
        { path: 'account', lazy: () => import('@/admin/AccountPage') },
        {
          path: 'users',
          middleware: [requireRole('admin')],
          lazy: () => import('@/admin/UsersAdmin'),
        },
        { path: 'messages', lazy: () => import('@/admin/MessagesAdmin') },
        { path: 'applications', lazy: () => import('@/admin/ApplicationsAdmin') },
      ],
    },
  ],
};

// Albanian first so `/sq/*` isn't swallowed by the English `:legacy` catch-all.
export const routes: RouteObject[] = [adminRoutes, ...[...LOCALES].reverse().map(siteRoutes)];

export const router = createBrowserRouter(routes);
